import uuid from 'node-uuid';
import {
  extendObservable,
  action,
  computed,
  autorun,
  observable,
  toJS
} from 'mobx';
import { snakeCase } from '@ridi/object-case-converter';
import axios, { setAxiosRootStoreAlertHandler } from '../../../libs/axios_csrf';
import groupBy from 'lodash/groupBy';
import camelCase from 'lodash/camelCase';

import {
  CARD_RELATIONS,
  LABEL_COLORS,
  LANE_NAMES,
  PRIORITIES,
  TODO_ITEM_STATUSES,
  TODO_ITEM_STATUSES_TO_INTEGER,
  WEEK_DAYS,
  COMPLEXITY
} from '../constants';
import {
  calculateNewPosition,
  isUUID4
} from '../../common_components/functions';
import {
  genUpdatedNestedAttributes,
  getDifferentObjects
} from '../../common_components/nested_attributes_helpers';
import { isSameDay } from '../../common_components/date_functions';

export default class Card {
  laneStore;
  id;

  constructor(
    laneStore,
    {
      id = uuid.v4(),
      name = '',
      description = '',
      creator = laneStore.rootStore.user,
      priority = PRIORITIES.LOW,
      lastUser = laneStore.rootStore.user,
      comments = [],
      attachments = [],
      assignee = laneStore.rootStore.user,
      complexity,
      position = laneStore.notesCount,
      createdAt = Date.now(),
      updatedAt = Date.now(),
      finishDate,
      todoList,
      labels = [],
      cardsLabels = [],
      cardsMemberships = [],
      teamAssignees = [],
      sourceCardsRelations = [],
      targetCardsRelations = [],
      cardAlarm,
      boardableName = '',
      priorBoard = ''
    },
    persisted = true
  ) {
    this.laneStore = laneStore;
    this.persisted = persisted;
    this.id = id;
    this.creator = creator;
    this.creatorId = creator.id;
    this.createdAt = createdAt;
    this.updatedAt = updatedAt;
    this.position = position;

    this.commentFormTextAreaRef = undefined;

    extendObservable(this, {
      name: name || '',
      description: description || '',
      lastUser: lastUser,
      comments: comments,
      // attachments: attachments,
      attachments: laneStore.rootStore.attachmentsStore.attachmentsFromJsonArray(
        attachments,
        this
      ),
      attachmentFilesQueue: [],
      assignee: assignee,
      priority: priority,
      complexity,
      finishDate: finishDate,
      todoList: todoList,
      showTodoManagePanel: false,
      showLabelManagePanel: false,
      labels: labels,
      cardsLabels: cardsLabels,
      allActivities: [],
      newCommentText: '',
      activityLoading: true,
      cardsMemberships: cardsMemberships,
      teamAssignees: teamAssignees,
      sourceCardsRelations: sourceCardsRelations,
      targetCardsRelations: targetCardsRelations,
      noteAlarm: cardAlarm,
      boardableName: boardableName,
      priorBoard: priorBoard
    });

    setAxiosRootStoreAlertHandler(this.laneStore.rootStore);
    this.handleNoteAlarm();
  }

  @action
  addAttachment(file) {
    this.attachmentFilesQueue.push(file);
    // Save attachments only if the attachable has already been persisted in backend.
    if (this.persisted) {
      this.persistAttachments();
    }
  }

  @action
  removeFileFromQueue(file) {
    this.attachmentFilesQueue.remove(file);
  }

  @action
  persistAttachments() {
    if (this.persisted) {
      while (this.attachmentFilesQueue.length > 0) {
        const file = this.attachmentFilesQueue.pop();
        this.laneStore.rootStore.attachmentsStore.create(file, this);
      }
    } else {
      throw 'AttachmentOnInvalidObjectException';
    }
  }

  canEditAttachments() {
    return this.laneStore.rootStore.authorizationStore.canCreateAttachment(
      this
    );
  }

  @action
  destroy() {
    if (!this._isPersisted()) {
      return;
    }

    // ------------------  AJAX CALL ---------------
    axios
      .delete(`/cards/${this.id}.json`)
      .then(
        action(response => {
          this.laneStore.rootStore.triggerChartUpdates();
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------

    this.laneStore._decrementNotesCounter();

    this.laneStore.notes.remove(this);
  }

  @computed get clone() {
    return Object.assign({}, this);
  }

  @action
  editNotePriority(priority) {
    if (!this._isPersisted()) {
      return;
    }

    this.priority = priority;

    let position = undefined;
    if (priority === PRIORITIES.MEDIUM || priority === PRIORITIES.HIGH) {
      position = -1;
    }

    //console.log(note.priority);

    // ------------------  AJAX CALL ---------------
    axios
      .patch(`/cards/${this.id}.json`, {
        card: {
          priority: this.priority,
          position: position
        }
      })
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
    if (this.laneStore.name !== LANE_NAMES.DONE) {
      this.laneStore.notes = this.laneStore.sortNotes(this.laneStore.notes);
    }
  }

  @action
  editNoteComplexity(complexity) {
    if (!this._isPersisted()) {
      return;
    }

    this.complexity = complexity;

    // ------------------  AJAX CALL ---------------
    axios
      .patch(`/cards/${this.id}.json`, {
        card: { complexity: this.complexity }
      })
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
    if (this.laneStore.name !== LANE_NAMES.DONE) {
      this.laneStore.notes = this.laneStore.sortNotes(this.laneStore.notes);
    }
  }

  @computed get creatorShortName() {
    return this.creator && this.creator.name.split(' ')[0];
  }

  @action
  editNoteDescription(description) {
    if (!this._isPersisted()) {
      return;
    }

    this.description = description;

    // ------------------  AJAX CALL ---------------
    axios
      .patch(`/cards/${this.id}.json`, {
        card: {
          description: this.description
        }
      })
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  @action
  editNoteName(name) {
    if (!this._isPersisted()) {
      return;
    }

    this.name = name;
    // ------------------  AJAX CALL ---------------
    axios
      .patch(`/cards/${this.id}.json`, {
        card: {
          name: this.name
        }
      })
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  @action moveNoteToLane(board, internal) {
    if (!this._isPersisted()) {
      return;
    }

    // move between lanes in the same board
    if (internal) {
      const laneId = board.id;
      const laneStore = this.rootStore.getLaneStoreById(laneId);

      if (laneStore) {
        laneStore.moveNoteFromDropdown(this);
      }
    } else {
      // convert lane name to mvps board list's name format, i.e: from 'to_do' to 'toDoListId'
      const laneId = board[`${camelCase(this.laneStore.name)}ListId`];

      // ------------------  AJAX CALL ---------------
      axios
        .patch(`/cards/${this.id}.json`, {
          card: {
            list_id: laneId,
            position: 0
          }
        })
        .then(
          action(response => {
            // console.log(response);
            this.rootStore.generateAlert({
              type: 'success',
              headline: 'card_moved_success_alert'
            });
          })
        )
        .catch(
          action(error => {
            console.log(error);
          })
        );

      //------------------------------------------------
      this.laneStore._decrementNotesCounter();
      this.laneStore.notes.remove(this);
    }
  }

  @action
  editDueDate(finishDate) {
    if (!this._isPersisted()) {
      return;
    }

    this.finishDate = finishDate;

    // ------------------  AJAX CALL ---------------
    axios
      .patch(`/cards/${this.id}.json`, {
        card: {
          finish_date: this.finishDate
        }
      })
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  @action
  editAssignee(member) {
    if (!this._isPersisted()) {
      return;
    }

    this.assignee = member;
    const memberId = member ? member.id : null;

    axios
      .patch(`/cards/${this.id}.json`, {
        card: {
          assignee_id: memberId
          // cards_memberships_attributes: [{user_id: 3}, {user_id: 13}]
        }
      })
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );
  }
  /** 
  @action
  addNewMemberToTeamAssignees(member) {
    if (!this._isPersisted()) {
      return;
    }

    this.teamAssignees.push(member);
    this.editTeamAssignees(this.teamAssignees)
  }
*/
  @action
  editTeamAssignees(members) {
    if (!this._isPersisted()) {
      return;
    }

    const cards_memberships_attributes = genUpdatedNestedAttributes(
      this.teamAssignees,
      this.cardsMemberships,
      members,
      'userId'
    );

    this.teamAssignees = members;

    axios
      .patch(`/cards/${this.id}.json`, {
        card: {
          cards_memberships_attributes
        }
      })
      .then(
        action(response => {
          this.cardsMemberships = response.data.cardsMemberships;
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );
  }

  @computed get allMembersWithoutAssignee() {
    return getDifferentObjects(this.rootStore.allMembers, [this.assignee]);
  }

  @computed get allMembersWithoutTeamAssignees() {
    return getDifferentObjects(this.rootStore.allMembers, this.teamAssignees);
  }

  find(notes, id) {
    const note = notes.find(note => note.id === id);

    if (!note) {
      console.warn('Failed to find note: ' + id);
    }

    return note;
  }

  /*
    findComment(id, note){
        const comment = note.comments.find(comment => comment.id === id); // find is es7 'polyfill' provided by reactive arrays

        if (!comment) {
            console.warn('Failed to find note: ' + id);
        }

        return comment;
    }
    */

  load() {
    /*
        return storage.get('Card') || [];
        */
  }

  persist() {
    /*
        // Whenever the Json representation of the notes changes, store them.
        autorun(() => {
            storage.set('Card', this.toJson);
        });
        */
  }

  @action
  addComment() {
    if (!this._isPersisted()) {
      return;
    }

    const comment = observable({
      id: uuid.v4(),
      body: this.newCommentText,
      creatorId: this.laneStore.rootStore.user.id,
      creator: this.laneStore.rootStore.user,
      createdAt: Date.now()
    });

    this.comments.unshift(comment);
    this.addCommentActivity(comment);

    this.newCommentText = '';

    // ------  AJAX CALL ----
    axios
      .post('/card_comments.json', {
        card_comment: {
          body: comment.body,
          card_id: this.id
        }
      })
      .then(
        action(response => {
          comment.id = response.data.id;
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  @action
  addCommentActivity(comment) {
    const activity = {
      id: uuid.v4(),
      description: '',
      key: 'card_comment.create',
      createdAt: Date.now(),
      user: this.rootStore.user,
      trackable: comment
    };

    this.allActivities.unshift(activity);
  }

  @action
  editComment(comment, value) {
    if (!this._isPersisted(comment)) {
      return;
    }

    if (!comment < 0) {
      return;
    }

    comment.body = value;

    // ------------------  AJAX CALL ---------------
    axios
      .patch(`/card_comments/${comment.id}.json`, {
        card_comment: {
          body: comment.body
        }
      })
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  @action
  deleteComment(comment) {
    if (!this._isPersisted(comment)) {
      return;
    }

    // ------------------  AJAX CALL ---------------
    axios
      .delete(`/card_comments/${comment.id}.json`)
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------

    this.comments.remove(comment);
  }

  // @action
  // addAttachment(attachment_data) {
  //
  //
  //     // ------  AJAX CALL ----
  //     axios.post('/attachments.json', attachment_data).then(action((response) => {
  //         this.attachments.push(
  //             {
  //                 id: response.data.id,
  //                 attachmentUrl: response.data.attachment_url,
  //                 attachmentName: response.data.attachment_name,
  //                 externalLink: response.data.external_link,
  //                 creatorId: response.data.creator_id,
  //                 creator: response.data.creator,
  //                 hidePreview: response.data.hide_preview,
  //             });
  //
  //     })).catch(action((error) => {
  //         // console.log("error.attach: ", error.response.data.attach[0]);
  //         this.rootStore.generateAlert({type: 'danger', headline: "attachment_error"});
  //
  //     }));
  //
  //     //------------------------------------------------
  //
  // }

  @action
  toggleAttachmentHidePreview(attachment) {
    attachment.hidePreview = !attachment.hidePreview;

    // ------------------  AJAX CALL ---------------
    axios
      .patch(`/attachments/${attachment.id}.json`, {
        attachment: {
          hide_preview: attachment.hidePreview
        }
      })
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  @action
  deleteAttachment(attachment) {
    // const attachmentIndex = this.attachments.indexOf(attachment);

    // if (attachmentIndex < 0) {
    //     return;
    // }

    //attachments.splice(attachmentIndex, 1);

    // ------------------  AJAX CALL ---------------
    axios
      .delete(`/attachments/${attachment.id}.json`)
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------

    this.attachments.remove(attachment);
  }

  @action
  addTodoItem(description) {
    const todo = observable({
      id: uuid.v4(),
      cardTodoListId: this.todoList.id,
      description: description,
      status: 'todo',
      position: this.todoList.items.length
    });

    this.todoList.items.push(todo);

    // ------  AJAX CALL ----
    axios
      .post('/card_todo_list_items.json', {
        card_todo_list_item: snakeCase(todo, true)
      })
      .then(
        action(response => {
          todo.id = response.data.id;
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  // add a new todos list to the valueProposition todos lists
  @action
  addTodoList(name) {
    const todoList = observable({ id: uuid.v4(), name: name, items: [] });
    this.rootStore.valueProposition.cardTodoLists.push(todoList);

    // ------------------  AJAX CALL ---------------
    axios
      .post(`/card_todo_lists.json`, {
        card_todo_list: {
          source_list_id: this.todoList.id,
          name: name,
          card_todo_listable_id: this.laneStore.rootStore.valueProposition.id,
          card_todo_listable_type: 'ValueProposition'
        }
      })
      .then(
        action(response => {
          todoList.id = response.data.id;
          todoList.items = response.data.items;
          console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  @action
  updateTodoListItems(todoList) {
    if (!this._isPersisted(todoList)) {
      return;
    }

    if (!todoList) {
      return;
    }

    // ------------------  AJAX CALL ---------------
    axios
      .patch(`/card_todo_lists/${todoList.id}.json`, {
        card_todo_list: {
          clone_items_from_list_id: this.todoList.id,
          overwrite_items: true
        }
      })
      .then(
        action(response => {
          todoList.items = response.data.items;
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  @action
  loadTodoListItems(todoList) {
    if (!this._isPersisted(todoList)) {
      return;
    }
    // this.todoList = todoList;

    // ------------------  AJAX CALL ---------------
    axios
      .patch(`/card_todo_lists/${this.todoList.id}.json`, {
        card_todo_list: {
          clone_items_from_list_id: todoList.id
        }
      })
      .then(
        action(response => {
          this.todoList.items = response.data.items;
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  @action
  deleteTodoListItems() {
    this.todoList.items = [];
    // ------------------  AJAX CALL ---------------
    axios
      .patch(`/card_todo_lists/${this.todoList.id}.json`, {
        card_todo_list: {
          destroy_all_items: true
        }
      })
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  @action
  deleteTodoList(todoList) {
    if (!this._isPersisted(todoList)) {
      return;
    }

    this.rootStore.valueProposition.cardTodoLists.remove(todoList);

    // ------------------  AJAX CALL ---------------
    axios
      .delete(`/card_todo_lists/${todoList.id}.json`, {})
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  @action
  editTodoItem(todoItem, descr) {
    if (!this._isPersisted(todoItem)) {
      return;
    }

    todoItem.description = descr;

    // ------------------  AJAX CALL ---------------
    axios
      .patch(`/card_todo_list_items/${todoItem.id}.json`, {
        card_todo_list_item: {
          description: todoItem.description
        }
      })
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  @action
  deleteTodoItem(todoItem) {
    if (!this._isPersisted(todoItem)) {
      return;
    }

    // ------------------  AJAX CALL ---------------
    axios
      .delete(`/card_todo_list_items/${todoItem.id}.json`)
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------

    this.todoList.items.remove(todoItem);
  }

  @action
  toggleItemStatus(todoItem) {
    if (!this._isPersisted(todoItem)) {
      return;
    }

    let nextStatus = Object.values(TODO_ITEM_STATUSES)[
      TODO_ITEM_STATUSES_TO_INTEGER[todoItem.status] + 1
    ];
    if (!nextStatus) {
      nextStatus = TODO_ITEM_STATUSES.TODO;
    }

    todoItem.status = nextStatus;

    // if item changed to doing and its card doesn't belong to doing and done lane, move it to doing
    if (
      nextStatus === TODO_ITEM_STATUSES.DOING &&
      !this._belongsToDoingOrDoneLaneStore
    ) {
      this._moveNoteToDoing();
    }

    // ------------------  AJAX CALL ---------------
    axios
      .patch(`/card_todo_list_items/${todoItem.id}.json`, {
        card_todo_list_item: {
          status: todoItem.status
        }
      })
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  @action
  _moveNoteToDoing() {
    const doingLaneStore = this.rootStore.getLaneStoreByName(LANE_NAMES.DOING);
    if (doingLaneStore) {
      doingLaneStore.moveNoteFromDropdown(this);
    }
  }

  @computed get _belongsToDoingOrDoneLaneStore() {
    return [LANE_NAMES.DOING, LANE_NAMES.DONE].includes(this.laneStore.name);
  }

  @action
  updateTodoItemPosition(todoItem, index) {
    if (!this._isPersisted(todoItem)) {
      return;
    }

    const items = this.todoList.items;

    // const index = items.indexOf(todoItem);
    // let position = 0;
    todoItem.position = calculateNewPosition(items, index);

    // let topPosition = items[index - 1] ? items[index - 1].position : undefined;
    // let downPosition = items[index + 1] ? items[index + 1].position : undefined;
    //
    // if (topPosition && downPosition) {
    //     position = (Number(topPosition) + Number(downPosition)) / 2.0;
    // } else if (topPosition) {
    //     position = Number(topPosition) - 0.01;
    // } else if (downPosition) {
    //     position = Number(downPosition) + 0.01;
    // }

    // todoItem.position = position;

    // ------  AJAX CALL ----------------------------
    axios
      .patch(`/card_todo_list_items/${todoItem.id}.json`, {
        card_todo_list_item: {
          position: todoItem.position
        }
      })
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );
    //------------------------------------------------
  }

  @action
  addCardsLabel(label) {
    const cardsLabel = observable({
      id: uuid.v4(),
      cardId: this.id,
      labelId: label.id,
      active: true
    });

    this.cardsLabels.push(cardsLabel);
    this.labels.push(label);

    // ------------------  AJAX CALL ---------------
    axios
      .post(`/cards_labels.json`, {
        cards_label: {
          card_id: cardsLabel.cardId,
          label_id: cardsLabel.labelId
        }
      })
      .then(
        action(response => {
          cardsLabel.id = response.data.id;
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  @action
  updateCardsLabel(cardLabel) {
    if (!this._isPersisted(cardLabel)) {
      return;
    }

    cardLabel.active = !cardLabel.active;

    // ------  AJAX CALL ----------------------------
    axios
      .patch(`/cards_labels/${cardLabel.id}.json`, {
        cards_label: {
          active: cardLabel.active
        }
      })
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );
    //------------------------------------------------
  }

  getCardsLabel(label) {
    return this.cardsLabels.find(l => l.labelId === label.id);
  }

  @computed get activeLabels() {
    return this.labels.filter(label => this.isActiveLabel(label));
  }

  isActiveLabel(label) {
    const cardLabel = this.getCardsLabel(label);
    return cardLabel && cardLabel.active;
  }

  @action
  addVPLabel(name) {
    // prevent label's duplication
    if (this.rootStore.valueProposition.labels.find(l => l.name === name)) {
      return;
    }

    const color = LABEL_COLORS[Math.floor(Math.random() * LABEL_COLORS.length)];

    const label = observable({ id: uuid.v4(), name: name, color: color });
    this.rootStore.valueProposition.labels.push(label);

    // ------------------  AJAX CALL ---------------
    axios
      .post(`/labels.json`, {
        label: {
          name: label.name,
          color: label.color,
          labelable_id: this.rootStore.valueProposition.id,
          labelable_type: 'ValueProposition'
        }
      })
      .then(
        action(response => {
          label.id = response.data.id;
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  @action
  editVPLabelName(label, name) {
    if (!this._isPersisted(label)) {
      return;
    }

    label.name = name;

    // ------------------  AJAX CALL ---------------
    axios
      .patch(`/labels/${label.id}.json`, {
        label: {
          name: label.name
        }
      })
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  @action
  editVPLabelColor(label, color) {
    if (!this._isPersisted(label)) {
      return;
    }

    label.color = color;

    // ------------------  AJAX CALL ---------------
    axios
      .patch(`/labels/${label.id}.json`, {
        label: {
          color: label.color
        }
      })
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  @action
  deleteVPLabel(label) {
    if (!this._isPersisted(label)) {
      return;
    }

    // ------------------  AJAX CALL ---------------
    axios
      .delete(`/labels/${label.id}.json`)
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------

    this.rootStore.valueProposition.labels.remove(label);

    const noteLabel = this.labels.find(l => l.id === label.id);
    if (noteLabel) {
      this.labels.remove(noteLabel);
    }
  }

  @action
  addNoteRelation(targetNote, relation) {
    if (!this._isPersisted(targetNote)) {
      return;
    }

    //if relation already exist, dont add it again
    if (
      this.sourceCardsRelations.find(
        r => r.targetCardId === targetNote.id && r.relation === relation
      )
    ) {
      return;
    }

    const noteRelation = observable({
      id: uuid.v4(),
      sourceCardId: this.id,
      targetCardId: targetNote.id,
      relation: relation,
      targetCardText: targetNote.name || targetNote.description
    });

    this.sourceCardsRelations.push(noteRelation);
    targetNote.targetCardsRelations.push(noteRelation);

    // ------------------  AJAX CALL ---------------
    axios
      .post(`/cards_relations.json`, {
        cards_relation: {
          source_card_id: noteRelation.sourceCardId,
          target_card_id: noteRelation.targetCardId,
          relation: noteRelation.relation
        }
      })
      .then(
        action(response => {
          noteRelation.id = response.data.id;
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  @action
  deleteNoteRelation(relation) {
    if (!this._isPersisted(relation)) {
      return;
    }

    // ------------------  AJAX CALL ---------------
    axios
      .delete(`/cards_relations/${relation.id}.json`)
      .then(
        action(response => {
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------

    const targetNote = this.rootStore.findNoteById(relation.targetCardId);
    if (targetNote) {
      const targetRelation = targetNote.targetCardsRelations.find(
        re => re.id === relation.id
      );
      if (targetRelation) {
        targetNote.targetCardsRelations.remove(targetRelation);
      }
    }
    this.sourceCardsRelations.remove(relation);
  }

  @action
  toggleLabelsManagePanel() {
    this.showLabelManagePanel = !this.showLabelManagePanel;
  }

  @action
  toggleTodoManagePanel() {
    this.showTodoManagePanel = !this.showTodoManagePanel;
  }

  @action
  updateLastUser(user = this.laneStore.rootStore.user) {
    this.lastUser = user;
  }

  // updateLabel(label, description) {
  //
  //     label.description = description;
  //
  //     // ------------------  AJAX CALL ---------------
  //     axios.patch('/cards/' + label.card_id + '.json', {
  //         card: {
  //             labels_attributes: {id: label.id, description: label.description}
  //         }
  //     }).then(function (response) {
  //         console.log(response);
  //     })
  //         .catch(function (error) {
  //             console.log(error);
  //         });
  //
  //     //------------------------------------------------
  // }

  @computed get cardTodoLists() {
    if (
      this.rootStore.valueProposition &&
      this.rootStore.valueProposition.cardTodoLists
    ) {
      return this.rootStore.valueProposition.cardTodoLists;
    } else {
      return [];
    }
  }

  @computed get rootStore() {
    return this.laneStore.rootStore;
  }

  @action
  fetchActivities() {
    if (!this._isPersisted()) {
      return;
    }

    // ------------------  AJAX CALL ---------------
    axios
      .get(`/card_activities/${this.id}`, {})
      .then(
        action(response => {
          // console.log(response);
          this.allActivities = response.data;
          this.activityLoading = false;
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );
    //------------------------------------------------
  }

  @action
  updateNoteAlarm({ cloneNote = false } = {}) {
    if (!this._isPersisted() || !this.noteAlarm) {
      return;
    }

    // ------------------  AJAX CALL ---------------
    axios
      .patch(`/cards/${this.id}.json`, {
        card: {
          card_alarm_attributes: { ...snakeCase(this.noteAlarm, true) }
        }
      })
      .then(
        action(response => {
          if (cloneNote) {
            this.cloneNote();
          }
          // console.log(response);
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  @computed get selectableLanesOptions() {
    return this.rootStore.lanes.map(lane => ({
      label: this.rootStore.localeStore.intl.formatMessage({ id: lane.name }),
      value: lane,
      disabled: this.laneStore.id === lane.id
    }));
  }

  @computed get groupedSourceCardsRelations() {
    return groupBy(this.sourceCardsRelations, 'relation');
  }

  @computed get blocked() {
    return !!(
      this.targetCardsRelations.find(
        relation => relation.relation === CARD_RELATIONS.BLOCKS
      ) ||
      this.sourceCardsRelations.find(
        relation => relation.relation === CARD_RELATIONS.BLOCKED_BY
      )
    );
  }

  @computed get labelsNames() {
    return this.labels.map(label => label.name.toLowerCase());
  }

  get todosDescriptions() {
    try {
      if (!this._todosDescriptions) {
        this._todosDescriptions = this.todoList.items.map(item =>
          item.description.toLowerCase()
        );
      }

      return this._todosDescriptions;
    } catch (e) {
      return [];
    }
  }

  get commentsBodies() {
    if (!this._commentsBodies) {
      this._commentsBodies = this.comments.map(comment =>
        comment.body.toLowerCase()
      );
    }

    return this._commentsBodies;
  }

  @action
  handleNoteAlarm() {
    if (
      !this.noteAlarm ||
      !this.persisted ||
      !this.rootStore.authorizationStore.canEditNote(this)
    ) {
      return;
    }

    const alarmCreationDate = new Date(this.noteAlarm.createdAt);
    const currentDate = new Date();
    const currentDay = currentDate.getDay();

    // same day, aka-still in the same creation day
    if (isSameDay(alarmCreationDate, currentDate)) {
      return;
    }

    // today is marked as an alarm day
    if (this.noteAlarm[Object.values(WEEK_DAYS)[currentDay]]) {
      // if the card doesn't belongs to to-do lane
      if (
        this.laneStore.name !== LANE_NAMES.TO_DO &&
        this.laneStore.name !== LANE_NAMES.TO_EXECUTE
      ) {
        // duplicate the card and insert it in to-do
        // disable the alarm if it's not a weekly alarm
        if (!this.noteAlarm.repeatWeekly) {
          this.noteAlarm[Object.values(WEEK_DAYS)[currentDay]] = false;
          this.updateNoteAlarm({ cloneNote: true });
        } else {
          this.cloneNote();
        }
      }
    }
  }

  @action
  cloneNote() {
    const todoLaneStore = this.rootStore.todoLaneStore;

    // ------------------  AJAX CALL ---------------
    axios
      .patch(`/cards/${this.id}/clone.json`, {
        card: {
          list_id: todoLaneStore.id
        }
      })
      .then(
        action(response => {
          // console.log("cloned", response);
          const newNote = new Card(todoLaneStore, response.data, true);
          todoLaneStore.notes.unshift(newNote);
          this.disableNoteAlarm();
        })
      )
      .catch(
        action(error => {
          console.log(error);
        })
      );

    //------------------------------------------------
  }

  @action
  disableNoteAlarm() {
    Object.values(WEEK_DAYS).forEach(day => (this.noteAlarm[day] = false));
    this.noteAlarm.repeatWeekly = false;

    this.updateNoteAlarm();
  }

  // check if this and if provided the object are persisted in the backend
  _isPersisted(object = undefined) {
    // if note (parent) is not persisted return false
    if (!this.persisted) {
      return false;
    }
    // if an object is provided check if it has numeric ID (aka: not an uuid.v4() id)
    if (object) {
      return object.id && !isUUID4(object.id);
    } else {
      return true;
    }
  }
}

// Required by the Attachments store
Card.backendModelName = 'Card';
